import React, { useEffect, useState } from 'react';
import { Modal as AntModal, Button as AntButton, message, Tabs as AntTabs } from 'antd';
import { getCompanySmtpSettings, sendEmail } from '../services/smtpService';
import { getUserCompanyInfo } from '../services/apiService';

export default function QuoteEmailModal({ open, onClose, recipientEmail, quoteName, period, pdfUrl }) {
  const [smtpSettings, setSmtpSettings] = useState(null);
  const [company, setCompany] = useState(null);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  const getDefaultEmailBody = (url, companyName, title, eventPeriod) => {
    return `<!DOCTYPE html>
<html>
  <body style="font-family: Pretendard, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Malgun Gothic', Arial, sans-serif; color:#111827; line-height:1.7; font-size:14px; margin:0; padding:0;">
    <div style="max-width:640px; margin:0 auto; padding:16px;">
      <p style="margin:0 0 12px 0;">안녕하세요.</p>
      <p style="margin:0 0 20px 0;">${companyName || '저희 여행사'}입니다.<br/>요청하신 견적서를 발송해 드립니다.</p>

      <div style="background:#f9fafb; border:1px solid #e5e7eb; border-radius:8px; padding:12px; margin:0 0 16px 0;">
        <div style="margin:0 0 6px 0;"><strong style="color:#374151;">■ 견적명:</strong> ${title || '-'}</div>
        <div><strong style="color:#374151;">■ 여행기간:</strong> ${eventPeriod || '-'}</div>
      </div>

      <p style="margin:0 0 12px 0;">아래 버튼을 클릭하여 견적서를 확인하실 수 있습니다.</p>
      <p style="margin:0 0 20px 0;">
        <a href="${url || '#'}" target="_blank" style="display:inline-block; background:#2563eb; color:#ffffff; text-decoration:none; padding:10px 16px; border-radius:8px; font-weight:600;">견적서 열기</a>
      </p>

      ${url ? `<p style="margin:0 0 20px 0; color:#6b7280; font-size:12px;">버튼이 보이지 않으면 아래 링크를 복사하여 브라우저에 붙여넣기 해주세요.<br/><span style="word-break:break-all;">${url}</span></p>` : ''}

      <p style="margin:0 0 12px 0;">문의사항이 있으시면 언제든지 연락 주시기 바랍니다.</p>
      <p style="margin:0 0 24px 0;">감사합니다.</p>
      <p style="margin:0;">${companyName || ''}</p>
    </div>
  </body>
</html>`;
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const smtp = await getCompanySmtpSettings();
        setSmtpSettings(smtp || null);
      } catch (e) {
        console.warn('SMTP 설정 조회 실패:', e);
      }
      try {
        const info = await getUserCompanyInfo();
        setCompany(info || null);
      } catch (e) {
        console.warn('회사 정보 조회 실패:', e);
      }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const defaultRecipient = String(recipientEmail || '').trim();
    const title = quoteName || '견적서';
    setEmailRecipient(defaultRecipient);
    setEmailSubject(`[견적서] ${title}`);
    setEmailBody(getDefaultEmailBody(pdfUrl, company?.업체명, title, period));
  }, [open, recipientEmail, quoteName, period, pdfUrl, company?.업체명]);

  const handleSend = async () => {
    if (!emailRecipient?.trim()) { message.error('수신자 이메일을 입력해주세요.'); return; }
    if (!emailSubject?.trim()) { message.error('메일 제목을 입력해주세요.'); return; }
    if (!smtpSettings) { message.error('SMTP 설정이 없습니다.'); return; }
    try {
      setSendingEmail(true);
      const hide = message.loading('이메일 발송 중...', 0);
      const recipients = emailRecipient.split(/[,;]/).map(e => e.trim()).filter(Boolean);
      await sendEmail({
        recipients,
        subject: emailSubject,
        body: emailBody,
        smtpServer: smtpSettings?.서버주소 || '',
        smtpPort: smtpSettings?.포트 || 465,
        smtpUsername: smtpSettings?.메일주소 || '',
        smtpPassword: smtpSettings?.비밀번호 || '',
      });
      hide();
      message.success('이메일이 성공적으로 발송되었습니다.');
      onClose && onClose();
    } catch (e) {
      console.error('견적 이메일 발송 실패:', e);
      message.error(e?.message || '이메일 발송에 실패했습니다.');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      footer={null}
      title={
        <div className="flex items-center">
          <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          견적서 이메일 발송
        </div>
      }
      width={640}
      destroyOnClose
      maskClosable={false}
    >
      <div className="space-y-4 pt-2">
        <div className={`p-3 rounded-lg text-sm ${smtpSettings ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
          {smtpSettings ? (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              SMTP 설정 완료 ({smtpSettings?.서버주소})
            </div>
          ) : (
            <div className="flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              SMTP 설정이 필요합니다. 설정 &gt; 발신메일 관리에서 설정해주세요.
            </div>
          )}
        </div>

      

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">수신자 이메일 <span className="text-red-500">*</span></label>
          <input
            className="w-full border border-gray-200 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
            value={emailRecipient}
            onChange={(e) => setEmailRecipient(e.target.value)}
            placeholder="example@email.com (여러 개는 쉼표/세미콜론 구분)"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">메일 제목 <span className="text-red-500">*</span></label>
          <input
            className="w-full border border-gray-200 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="메일 제목을 입력하세요"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">메일 본문</label>
          <AntTabs
            defaultActiveKey="preview"
            items={[
              {
                key: 'edit',
                label: '편집',
                children: (
                  <textarea
                    className="w-full border border-gray-200 rounded-lg shadow-sm py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm h-48 font-mono"
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    placeholder="메일 본문을 입력하세요 (HTML 태그 사용 가능)"
                  />
                ),
              },
              {
                key: 'preview',
                label: '미리보기',
                children: (
                  <div className="border border-gray-200 rounded-lg bg-white p-4 max-h-96 overflow-auto" dangerouslySetInnerHTML={{ __html: emailBody || '' }} />
                ),
              },
            ]}
          />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
          <AntButton onClick={onClose} size="large">취소</AntButton>
          <AntButton type="primary" onClick={handleSend} loading={sendingEmail} disabled={!smtpSettings} size="large">발송</AntButton>
        </div>
      </div>
    </AntModal>
  );
}
